import { createAction, props } from '@ngrx/store';

import { OrbiFeedbackType } from '@patient-ui/shared/enums';
import { OrbiFeedbackRequest, OrbiMessage } from '@patient-ui/shared/models';

import { OrbiError } from './orbi-error';

/**
 * Orbi chat actions (PCSA-13164) — the headless send/receive cycle. Follows the
 * repo's trigger / success / failure convention (see `results.actions.ts`).
 */

export const sendMessage = createAction(
  '[Orbi] Send Message',
  props<{ userMessage: string; requestContext: string[] }>()
);

/**
 * `sessionId` is the conversation the request was SENT against (PCSA-13312), not
 * whichever one is active when the answer arrives. The two can differ: the user
 * can start a new conversation while the agent is still thinking, and the
 * in-flight answer then belongs to a thread that is no longer on screen. The
 * reducer compares it and drops the stale turn instead of appending it under a
 * prompt that was cleared with the old conversation — the reported bug.
 */
export const sendMessageSuccess = createAction(
  '[Orbi] Send Message Success',
  props<{ message: OrbiMessage; sessionId: string }>()
);

/**
 * Carries the typed [OrbiError] (a justified deviation from the string-error
 * convention in other slices — `OrbiService` already classifies failures via
 * `mapOrbiError`, satisfying PCSA-13163 AC7) plus the originating `sessionId`,
 * for the same reason `sendMessageSuccess` carries it.
 */
export const sendMessageFailure = createAction(
  '[Orbi] Send Message Failure',
  props<{ error: OrbiError; sessionId: string }>()
);

/**
 * Starts a fresh conversation: regenerates the sessionId and clears the active
 * (in-memory) messages. Prior conversations live server-side and are NOT
 * destroyed. Collapses mobile's reset/startNewChat/returnToHome/resetSessionId
 * (their extra UI fields belong to PCSA-13160/13174).
 */
export const startNewConversation = createAction(
  '[Orbi] Start New Conversation'
);

/** Resubmits the last user message with its original request context. */
export const retryLastMessage = createAction('[Orbi] Retry Last Message');

/**
 * The "How does this work?" chip was chosen (PCSA-13159 AC 6): append the
 * explainer as a real CONVERSATION TURN — the chip's label as the user's message,
 * the approved explainer as the agent's answer (design review 08/11).
 *
 * No transport: the pair is composed entirely from constants, so there is no
 * effect behind this action and nothing is sent to the agent. It is an action
 * rather than local component state because the thread renders from the store —
 * only turns in `messages` are drawn as bubbles, and only a non-empty `messages`
 * retires the suggested prompts.
 */
export const explainHowItWorks = createAction('[Orbi] Explain How It Works');

/**
 * Resumes a stored conversation (PCSA-13169): swaps the active sessionId and
 * replaces the thread with the conversation's messages. `locked` reflects the
 * read-only (non-soft guardrail) inference so the resumed thread keeps its lock
 * state (lock ENFORCEMENT UI is PCSA-13170).
 *
 * `startedAt` carries the conversation's `metadata.createdAt` so the thread can
 * date itself the way the history row does (design review 08/20) — the service
 * exposes no per-turn timestamp, so this is the only date there is. Optional
 * because a conversation adopted without its metadata is indistinguishable from
 * one the service has not stamped yet, and both mean the same thing to the view.
 */
export const resumeConversation = createAction(
  '[Orbi] Resume Conversation',
  props<{
    sessionId: string;
    messages: OrbiMessage[];
    locked: boolean;
    startedAt?: string | null;
  }>()
);

/**
 * Dispatched by the effect when a `sendMessage` is dropped by the Split-flag
 * hard gate (PCSA-13165): the agent is disabled, so no service call is made.
 * The reducer reverts the optimistic Pending status + the appended user message
 * so the UI does not get stuck in a loading state (no success/failure follows).
 *
 * Session-tagged like the other two outcomes (PCSA-13312): the revert SLICES a
 * turn off the active thread, so it must never land on a conversation other than
 * the one whose send was blocked.
 */
export const sendMessageBlocked = createAction(
  '[Orbi] Send Message Blocked',
  props<{ sessionId: string }>()
);

/**
 * Response feedback (PCSA-13171): submit thumbs-up/down for a specific agent
 * response, tied to `messageId` within the active `sessionId`. The request body
 * carries the sentiment, optional/required comment, and (for negative) the
 * selected issue categories.
 */
export const submitOrbiFeedback = createAction(
  '[Orbi] Submit Feedback',
  props<{ messageId: string; feedback: OrbiFeedbackRequest }>()
);

export const submitOrbiFeedbackSuccess = createAction(
  '[Orbi] Submit Feedback Success',
  props<{ messageId: string; feedbackType: OrbiFeedbackType }>()
);

/** Carries the typed [OrbiError] + the target messageId so the row can retry. */
export const submitOrbiFeedbackFailure = createAction(
  '[Orbi] Submit Feedback Failure',
  props<{ messageId: string; error: OrbiError }>()
);

export const OrbiActions = {
  sendMessage,
  sendMessageSuccess,
  sendMessageFailure,
  startNewConversation,
  retryLastMessage,
  explainHowItWorks,
  resumeConversation,
  sendMessageBlocked,
  submitOrbiFeedback,
  submitOrbiFeedbackSuccess,
  submitOrbiFeedbackFailure,
};
